import React from 'react';
import { Camera, Plus, Trash2, MapPin, Cake, Briefcase, GraduationCap, Ruler, Upload, Sparkles } from 'lucide-react';
import PersonalityType from './PersonalityType';

import interestsData from './interestsData';

export default function ProfileView({
  profile, user, matches, loading, error, uploadingPhoto,
  editingProfile, setEditingProfile,
  expandedSections, setExpandedSections,
  profileForm, setProfileForm,
  surveyData, showSurvey, setShowSurvey,
  interestSearch, setInterestSearch,
  showInterestModal, setShowInterestModal,
  activeInterestTab, setActiveInterestTab,
  onSaveProfile, onDeletePhoto, onPhotoUpload,
  onSaveSurvey, onSurveyChange,
  toggleInterest, toggleGenderPreference,
  setCurrentView, onNeedsVerification,
}) {
  const isSurveyComplete = Object.values(surveyData).every(val => val !== '');
  const isAboutComplete = !!(profileForm.bio && profileForm.bio.trim().length > 0);
  const isPersonalityComplete = !!(profileForm.mbtiType && profileForm.mbtiType.length > 0);
  const isSurveyReallyComplete = isSurveyComplete;

  return (
          <div className="fade-in profile-scroll" style={{ padding: '20px', maxHeight: '70vh', overflowY: 'auto' }}>
            {console.log('🎨 RENDER: Edit Profile - profileForm state:', {
              smoking: profileForm.smoking,
              drinking: profileForm.drinking,
              marijuana: profileForm.marijuana
            })}
            
            {/* Profile Header with Avatar */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '16px', 
              marginBottom: '20px' 
            }}>
              <div style={{
                width: '56px',
                height: '56px',
                borderRadius: 'var(--radius-full)',
                overflow: 'hidden',
                border: '3px solid var(--primary)',
                flexShrink: 0
              }}>
                <img 
                  src={profileForm.photos?.[0]?.url || 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23e2e8f0" width="100" height="100"/><text x="50" y="55" text-anchor="middle" fill="%2394a3b8" font-size="40">?</text></svg>'}
                  alt="You"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: '20px', fontWeight: 700, margin: '0 0 2px 0', color: 'var(--text-primary)' }}>
                  {profile?.firstName || 'Your Profile'}
                </h3>
                <p style={{ color: 'var(--text-muted)', margin: 0, fontSize: '13px' }}>
                  Make a great first impression
                </p>
              </div>
            </div>

            {/* Profile Completion — only show if incomplete */}
            {profile && (() => {
              const missing = [];
              if (!profileForm.bio || profileForm.bio.trim() === '') missing.push({ field: 'bio', label: 'Add your bio' });
              if (!profileForm.interests || profileForm.interests.length === 0) missing.push({ field: 'interests', label: 'Select interests' });
              if (!profileForm.photos || profileForm.photos.length < 3) {
                const needed = profileForm.photos ? 3 - profileForm.photos.length : 3;
                missing.push({ field: 'photos', label: `Upload ${needed} more ${needed === 1 ? 'photo' : 'photos'}` });
              }
              if (!user?.isIdentityVerified) missing.push({ field: 'verification', label: 'Verify your identity' });
              
              const isComplete = missing.length === 0;
              
              // Only render if incomplete
              if (isComplete) return null;
              
              const total = 4;
              const done = total - missing.length;
              const pct = Math.round((done / total) * 100);
              
              return (
                <div style={{
                  background: 'var(--surface-dim)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  padding: '14px 16px',
                  marginBottom: '16px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)' }}>
                      {pct}% complete
                    </span>
                    <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                      {missing.length} remaining
                    </span>
                  </div>
                  <div style={{
                    height: '4px',
                    background: 'var(--border)',
                    borderRadius: '2px',
                    overflow: 'hidden',
                    marginBottom: '10px'
                  }}>
                    <div style={{
                      height: '100%',
                      width: `${pct}%`,
                      background: 'var(--primary)',
                      borderRadius: '2px',
                      transition: 'width 0.5s ease'
                    }} />
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                    <strong style={{ display: 'block', marginBottom: '6px', color: 'var(--text-primary)' }}>To do:</strong>
                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                      {missing.map((item, idx) => (
                        <li key={idx} style={{ marginBottom: '2px' }}>{item.label}</li>
                      ))}
                    </ul>
                  </div>
                  {missing.some(m => m.field === 'verification') && (
                    <button
                      onClick={() => onNeedsVerification()}
                      style={{
                        marginTop: '10px',
                        background: 'var(--primary)',
                        color: 'white',
                        border: 'none',
                        padding: '6px 14px',
                        borderRadius: 'var(--radius-full)',
                        fontSize: '12px',
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                    >
                      🔒 Verify Identity
                    </button>
                  )}
                </div>
              );
            })()}


            {error && (
              <div style={{
                background: '#fee',
                color: '#c33',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '20px',
                fontSize: '14px'
              }}>
                {error}
              </div>
            )}

            {/* Photos Section */}
            <div className="profile-section" style={{ marginBottom: '16px' }}>
              <button
                type="button"
                onClick={() => setExpandedSections({...expandedSections, photos: !expandedSections.photos})}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: 'none',
                  border: 'none',
                  padding: '0 0 12px 0',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'var(--text-primary)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Camera size={16} />
                  <span>Photos ({profileForm.photos.length}/6)</span>
                </div>
                <span style={{ fontSize: '18px', transition: 'transform 0.2s', transform: expandedSections.photos ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                  ▼
                </span>
              </button>

              {expandedSections.photos && (
                <>
                  {/* Warning for < 3 photos */}
                  {profileForm.photos.length < 3 && (
                    <div style={{
                      padding: '12px',
                      marginBottom: '16px',
                      background: 'rgba(245, 158, 11, 0.1)',
                      border: '1px solid #f59e0b',
                      borderRadius: '8px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                        <span style={{ fontSize: '20px' }}>⚠️</span>
                        <strong style={{ color: '#92400e', fontSize: '14px' }}>
                          Profile Hidden - {3 - profileForm.photos.length} more {3 - profileForm.photos.length === 1 ? 'photo' : 'photos'} required
                        </strong>
                      </div>
                      <p style={{ color: '#78350f', margin: 0, fontSize: '13px' }}>
                        You need at least 3 photos to appear in matches. Your profile won't be visible to others until you upload {3 - profileForm.photos.length} more.
                      </p>
                    </div>
                  )}
                  
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: '12px',
                    marginBottom: '16px'
                  }}>
                {profileForm.photos.map((photo, index) => (
                  <div
                    key={photo._id || photo.id || index}
                    className="photo-container"
                    style={{
                      position: 'relative',
                      aspectRatio: '1',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      border: index === 0 ? '3px solid #667eea' : '2px solid #e9ecef'
                    }}
                  >
                    <img
                      src={photo.url}
                      alt={`${index + 1}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                    {index === 0 && (
                      <div style={{
                        position: 'absolute',
                        top: '8px',
                        left: '8px',
                        background: '#667eea',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: 600
                      }}>
                        PRIMARY
                      </div>
                    )}
                    <button
                      className="photo-delete-btn"
                      onClick={() => onDeletePhoto(photo._id || photo.id)}
                      style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        background: 'rgba(255,0,0,0.8)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50%',
                        width: '32px',
                        height: '32px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}

                {profileForm.photos.length < 6 && (
                  <label style={{
                    aspectRatio: '1',
                    border: '2px dashed #e9ecef',
                    borderRadius: '12px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: uploadingPhoto ? 'not-allowed' : 'pointer',
                    background: uploadingPhoto ? '#f8f9fa' : 'white',
                    transition: 'all 0.2s'
                  }}>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={onPhotoUpload}
                      disabled={uploadingPhoto}
                      style={{ display: 'none' }}
                    />
                    {uploadingPhoto ? (
                      <>
                        <Upload size={24} color="#667eea" style={{ marginBottom: '4px', animation: 'pulse 1.5s ease-in-out infinite' }} />
                        <span style={{ fontSize: '11px', color: '#667eea' }}>Uploading...</span>
                      </>
                    ) : (
                      <>
                        <Plus size={24} color="#95a5a6" style={{ marginBottom: '4px' }} />
                        <span style={{ fontSize: '11px', color: '#95a5a6' }}>Add Photo</span>
                      </>
                    )}
                  </label>
                )}
              </div>

              <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: 0 }}>
                First photo = main profile pic. Max 5MB each.
              </p>
                </>
              )}
            </div>

            {/* About You Section — Bio + Details */}
            <div className="profile-section" style={{ marginBottom: '16px' }}>
              <button
                type="button"
                onClick={() => setExpandedSections({...expandedSections, about: !expandedSections.about})}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: 'none',
                  border: 'none',
                  padding: '0 0 12px 0',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'var(--text-primary)'
                }}
              >
                <span>✏️ About You {isAboutComplete ? '✓' : '(Incomplete)'}</span>
                <span style={{ fontSize: '18px', transition: 'transform 0.2s', transform: expandedSections.about ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                  ▼
                </span>
              </button>

              {expandedSections.about && (
                <>
              {/* Bio */}
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px', color: 'var(--text-primary)', fontSize: '13px' }}>
                Bio
              </label>
              <textarea
                value={profileForm.bio}
                onChange={(e) => setProfileForm({...profileForm, bio: e.target.value})}
                placeholder="Tell people about yourself..."
                className="spark-input"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box',
                  minHeight: '80px',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  transition: 'border-color 0.2s, box-shadow 0.2s'
                }}
              />

              {/* Occupation + Education side by side */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '14px' }}>
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 600, marginBottom: '6px', color: 'var(--text-primary)', fontSize: '13px' }}>
                    <Briefcase size={13} /> Work
                  </label>
                  <input
                    type="text"
                    value={profileForm.occupation}
                    onChange={(e) => setProfileForm({...profileForm, occupation: e.target.value})}
                    placeholder="Your job"
                    className="spark-input"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-md)',
                      fontSize: '14px',
                      outline: 'none',
                      boxSizing: 'border-box',
                      transition: 'border-color 0.2s, box-shadow 0.2s'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 600, marginBottom: '6px', color: 'var(--text-primary)', fontSize: '13px' }}>
                    <GraduationCap size={13} /> Education
                  </label>
                  <input
                    type="text"
                    value={profileForm.education}
                    onChange={(e) => setProfileForm({...profileForm, education: e.target.value})}
                    placeholder="Degree"
                    className="spark-input"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-md)',
                      fontSize: '14px',
                      outline: 'none',
                      boxSizing: 'border-box',
                      transition: 'border-color 0.2s, box-shadow 0.2s'
                    }}
                  />
                </div>
              </div>

              {/* Height */}
              <div style={{ marginTop: '14px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 600, marginBottom: '6px', color: 'var(--text-primary)', fontSize: '13px' }}>
                  <Ruler size={13} /> Height (cm)
                </label>
                <input
                  type="text"
                  value={profileForm.height}
                  onChange={(e) => setProfileForm({...profileForm, height: e.target.value})}
                  placeholder="e.g., 178"
                  className="spark-input"
                  style={{
                    width: '120px',
                    padding: '10px 12px',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box',
                    transition: 'border-color 0.2s, box-shadow 0.2s'
                  }}
                />
              </div>

              {/* Kids */}
              <div style={{ marginTop: '14px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 600, marginBottom: '8px', color: 'var(--text-primary)', fontSize: '13px' }}>
                  👶 Children
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {[
                    { value: 'have_want_more', label: 'Have kids, want more' },
                    { value: 'have_no_more', label: 'Have kids, done' },
                    { value: 'no_want', label: "Don't have, want kids" },
                    { value: 'no_no_want', label: "Don't have, don't want" },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setProfileForm({ ...profileForm, kids: profileForm.kids === opt.value ? '' : opt.value })}
                      style={{
                        padding: '7px 14px',
                        borderRadius: 'var(--radius-full)',
                        border: '1.5px solid',
                        borderColor: profileForm.kids === opt.value ? 'var(--primary)' : 'var(--border)',
                        background: profileForm.kids === opt.value ? 'var(--primary)' : 'var(--surface)',
                        color: profileForm.kids === opt.value ? 'white' : 'var(--text-secondary)',
                        fontSize: '12px',
                        fontWeight: 500,
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Religion */}
              <div style={{ marginTop: '14px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 600, marginBottom: '8px', color: 'var(--text-primary)', fontSize: '13px' }}>
                  🙏 Religion
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {[
                    'Christian', 'Catholic', 'Muslim', 'Jewish', 'Hindu', 'Buddhist',
                    'Sikh', 'Spiritual', 'Agnostic', 'Atheist', 'Other', 'Prefer not to say'
                  ].map(opt => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setProfileForm({ ...profileForm, religion: profileForm.religion === opt ? '' : opt })}
                      style={{
                        padding: '7px 14px',
                        borderRadius: 'var(--radius-full)',
                        border: '1.5px solid',
                        borderColor: profileForm.religion === opt ? 'var(--primary)' : 'var(--border)',
                        background: profileForm.religion === opt ? 'var(--primary)' : 'var(--surface)',
                        color: profileForm.religion === opt ? 'white' : 'var(--text-secondary)',
                        fontSize: '12px',
                        fontWeight: 500,
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              {/* Pets */}
              <div style={{ marginTop: '14px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 600, marginBottom: '8px', color: 'var(--text-primary)', fontSize: '13px' }}>
                  🐾 Pets
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {[
                    { value: 'dog', label: '🐕 Dog' },
                    { value: 'cat', label: '🐈 Cat' },
                    { value: 'both', label: '🐕🐈 Both' },
                    { value: 'other', label: '🐾 Other pets' },
                    { value: 'want', label: '💛 No pets, want one' },
                    { value: 'none', label: 'No pets' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setProfileForm({ ...profileForm, pets: profileForm.pets === opt.value ? '' : opt.value })}
                      style={{
                        padding: '7px 14px',
                        borderRadius: 'var(--radius-full)',
                        border: '1.5px solid',
                        borderColor: profileForm.pets === opt.value ? 'var(--primary)' : 'var(--border)',
                        background: profileForm.pets === opt.value ? 'var(--primary)' : 'var(--surface)',
                        color: profileForm.pets === opt.value ? 'white' : 'var(--text-secondary)',
                        fontSize: '12px',
                        fontWeight: 500,
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Smoking */}
              <div style={{ marginTop: '14px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 600, marginBottom: '8px', color: 'var(--text-primary)', fontSize: '13px' }}>
                  🚬 Smoking
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {[
                    { value: 'Never', label: 'Never' },
                    { value: 'Occasionally', label: 'Occasionally' },
                    { value: 'Socially', label: 'Socially' },
                    { value: 'Regularly', label: 'Regularly' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setProfileForm({ ...profileForm, smoking: profileForm.smoking === opt.value ? '' : opt.value })}
                      style={{
                        padding: '7px 14px',
                        borderRadius: 'var(--radius-full)',
                        border: '1.5px solid',
                        borderColor: profileForm.smoking === opt.value ? 'var(--primary)' : 'var(--border)',
                        background: profileForm.smoking === opt.value ? 'var(--primary)' : 'var(--surface)',
                        color: profileForm.smoking === opt.value ? 'white' : 'var(--text-secondary)',
                        fontSize: '12px',
                        fontWeight: 500,
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Drinking */}
              <div style={{ marginTop: '14px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 600, marginBottom: '8px', color: 'var(--text-primary)', fontSize: '13px' }}>
                  🍷 Drinking
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {[
                    { value: 'Never', label: 'Never' },
                    { value: 'Rarely', label: 'Rarely' },
                    { value: 'Socially', label: 'Socially' },
                    { value: 'Regularly', label: 'Regularly' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setProfileForm({ ...profileForm, drinking: profileForm.drinking === opt.value ? '' : opt.value })}
                      style={{
                        padding: '7px 14px',
                        borderRadius: 'var(--radius-full)',
                        border: '1.5px solid',
                        borderColor: profileForm.drinking === opt.value ? 'var(--primary)' : 'var(--border)',
                        background: profileForm.drinking === opt.value ? 'var(--primary)' : 'var(--surface)',
                        color: profileForm.drinking === opt.value ? 'white' : 'var(--text-secondary)',
                        fontSize: '12px',
                        fontWeight: 500,
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Marijuana */}
              <div style={{ marginTop: '14px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 600, marginBottom: '8px', color: 'var(--text-primary)', fontSize: '13px' }}>
                  🌿 Cannabis
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {[
                    { value: 'Never', label: 'Never' },
                    { value: 'Occasionally', label: 'Occasionally' },
                    { value: 'Socially', label: 'Socially' },
                    { value: 'Regularly', label: 'Regularly' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setProfileForm({ ...profileForm, marijuana: profileForm.marijuana === opt.value ? '' : opt.value })}
                      style={{
                        padding: '7px 14px',
                        borderRadius: 'var(--radius-full)',
                        border: '1.5px solid',
                        borderColor: profileForm.marijuana === opt.value ? 'var(--primary)' : 'var(--border)',
                        background: profileForm.marijuana === opt.value ? 'var(--primary)' : 'var(--surface)',
                        color: profileForm.marijuana === opt.value ? 'white' : 'var(--text-secondary)',
                        fontSize: '12px',
                        fontWeight: 500,
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
                </>
              )}
            </div>

            {/* Personality Type Section */}
            <div className="profile-section" style={{ marginBottom: '16px' }}>
              <button
                type="button"
                onClick={() => setExpandedSections({...expandedSections, personality: !expandedSections.personality})}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: 'none',
                  border: 'none',
                  padding: '0 0 12px 0',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'var(--text-primary)'
                }}
              >
                <span>🧩 Personality Type {isPersonalityComplete ? '✓' : '(Optional)'}</span>
                <span style={{ fontSize: '18px', transition: 'transform 0.2s', transform: expandedSections.personality ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                  ▼
                </span>
              </button>

              {expandedSections.personality && (
                <>
              <PersonalityType
                value={profileForm.mbtiType}
                onChange={(type) => setProfileForm({ ...profileForm, mbtiType: type })}
              />
                </>
              )}
            </div>

            {/* Compatibility Survey — collapsible card */}
            <div className="profile-section" style={{ marginBottom: '16px' }}>
              <button
                onClick={() => setShowSurvey(!showSurvey)}
                type="button"
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: 'none',
                  border: 'none',
                  padding: '0 0 12px 0',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'var(--text-primary)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Sparkles size={16} />
                  <span>✨ Compatibility Survey {isSurveyReallyComplete ? '✓' : '(Optional)'}</span>
                </div>
                <span style={{ fontSize: '18px', transition: 'transform 0.2s', transform: showSurvey ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                  ▼
                </span>
              </button>

              {showSurvey && (
                <>
                <div style={{ paddingTop: '12px' }}>
                <p style={{ color: 'var(--text-muted)', marginBottom: '20px', fontSize: '13px' }}>
                  Helps find better matches for you
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {/* Question 1 */}
                  <div>
                    <p style={{ fontWeight: 600, marginBottom: '10px', fontSize: '14px', color: 'var(--text-primary)' }}>
                      1. Social gatherings make you feel:
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          checked={surveyData.socialEnergy === 'energize'}
                          onChange={() => onSurveyChange('socialEnergy', 'energize')}
                        />
                        <span style={{ fontSize: '14px', color: 'var(--text-primary)' }}>Energized and excited</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          checked={surveyData.socialEnergy === 'recharge'}
                          onChange={() => onSurveyChange('socialEnergy', 'recharge')}
                        />
                        <span style={{ fontSize: '14px', color: 'var(--text-primary)' }}>Drained and needing to recharge</span>
                      </label>
                    </div>
                  </div>

                  {/* Question 2 */}
                  <div>
                    <p style={{ fontWeight: 600, marginBottom: '10px', fontSize: '14px', color: 'var(--text-primary)' }}>
                      2. Your ideal weekend involves:
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          checked={surveyData.weekend === 'adventure'}
                          onChange={() => onSurveyChange('weekend', 'adventure')}
                        />
                        <span style={{ fontSize: '14px', color: 'var(--text-primary)' }}>New adventures and exploring</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          checked={surveyData.weekend === 'relax'}
                          onChange={() => onSurveyChange('weekend', 'relax')}
                        />
                        <span style={{ fontSize: '14px', color: 'var(--text-primary)' }}>Relaxing at home with hobbies</span>
                      </label>
                    </div>
                  </div>

                  {/* Question 3 */}
                  <div>
                    <p style={{ fontWeight: 600, marginBottom: '10px', fontSize: '14px', color: 'var(--text-primary)' }}>
                      3. When conflicts arise, you prefer to:
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          checked={surveyData.conflict === 'discuss'}
                          onChange={() => onSurveyChange('conflict', 'discuss')}
                        />
                        <span style={{ fontSize: '14px', color: 'var(--text-primary)' }}>Discuss them immediately</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          checked={surveyData.conflict === 'space'}
                          onChange={() => onSurveyChange('conflict', 'space')}
                        />
                        <span style={{ fontSize: '14px', color: 'var(--text-primary)' }}>Take space to process first</span>
                      </label>
                    </div>
                  </div>

                  {/* Question 4 */}
                  <div>
                    <p style={{ fontWeight: 600, marginBottom: '10px', fontSize: '14px', color: 'var(--text-primary)' }}>
                      4. You tend to:
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          checked={surveyData.planning === 'plan'}
                          onChange={() => onSurveyChange('planning', 'plan')}
                        />
                        <span style={{ fontSize: '14px', color: 'var(--text-primary)' }}>Plan ahead and organize</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          checked={surveyData.planning === 'spontaneous'}
                          onChange={() => onSurveyChange('planning', 'spontaneous')}
                        />
                        <span style={{ fontSize: '14px', color: 'var(--text-primary)' }}>Go with the flow spontaneously</span>
                      </label>
                    </div>
                  </div>

                  {/* Question 5 */}
                  <div>
                    <p style={{ fontWeight: 600, marginBottom: '10px', fontSize: '14px', color: 'var(--text-primary)' }}>
                      5. What matters most to you in a relationship?
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          checked={surveyData.values === 'stability'}
                          onChange={() => onSurveyChange('values', 'stability')}
                        />
                        <span style={{ fontSize: '14px', color: 'var(--text-primary)' }}>Stability and security</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          checked={surveyData.values === 'growth'}
                          onChange={() => onSurveyChange('values', 'growth')}
                        />
                        <span style={{ fontSize: '14px', color: 'var(--text-primary)' }}>Growth and adventure</span>
                      </label>
                    </div>
                  </div>

                  {/* Question 6 */}
                  <div>
                    <p style={{ fontWeight: 600, marginBottom: '10px', fontSize: '14px', color: 'var(--text-primary)' }}>
                      6. Your communication style is:
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          checked={surveyData.communication === 'direct'}
                          onChange={() => onSurveyChange('communication', 'direct')}
                        />
                        <span style={{ fontSize: '14px', color: 'var(--text-primary)' }}>Direct and to the point</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          checked={surveyData.communication === 'think'}
                          onChange={() => onSurveyChange('communication', 'think')}
                        />
                        <span style={{ fontSize: '14px', color: 'var(--text-primary)' }}>Thoughtful and nuanced</span>
                      </label>
                    </div>
                  </div>

                  {/* Question 7 */}
                  <div>
                    <p style={{ fontWeight: 600, marginBottom: '10px', fontSize: '14px', color: 'var(--text-primary)' }}>
                      7. In your free time, you enjoy:
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          checked={surveyData.interests === 'active'}
                          onChange={() => onSurveyChange('interests', 'active')}
                        />
                        <span style={{ fontSize: '14px', color: 'var(--text-primary)' }}>Physical activities and sports</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          checked={surveyData.interests === 'creative'}
                          onChange={() => onSurveyChange('interests', 'creative')}
                        />
                        <span style={{ fontSize: '14px', color: 'var(--text-primary)' }}>Creative or intellectual pursuits</span>
                      </label>
                    </div>
                  </div>

                  {/* Question 8 */}
                  <div>
                    <p style={{ fontWeight: 600, marginBottom: '10px', fontSize: '14px', color: 'var(--text-primary)' }}>
                      8. Your ideal lifestyle pace is:
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          checked={surveyData.lifestyle === 'fast'}
                          onChange={() => onSurveyChange('lifestyle', 'fast')}
                        />
                        <span style={{ fontSize: '14px', color: 'var(--text-primary)' }}>Fast-paced and always on the go</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          checked={surveyData.lifestyle === 'balanced'}
                          onChange={() => onSurveyChange('lifestyle', 'balanced')}
                        />
                        <span style={{ fontSize: '14px', color: 'var(--text-primary)' }}>Balanced with time for rest</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          checked={surveyData.lifestyle === 'slow'}
                          onChange={() => onSurveyChange('lifestyle', 'slow')}
                        />
                        <span style={{ fontSize: '14px', color: 'var(--text-primary)' }}>Slow and intentional</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Save Survey Button */}
                <button
                  onClick={onSaveSurvey}
                  disabled={!isSurveyComplete}
                  style={{
                    width: '100%',
                    padding: '12px',
                    marginTop: '20px',
                    background: isSurveyComplete ? 'var(--primary)' : 'var(--border)',
                    color: isSurveyComplete ? 'white' : 'var(--text-muted)',
                    border: 'none',
                    borderRadius: 'var(--radius-md)',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: isSurveyComplete ? 'pointer' : 'not-allowed'
                  }}
                >
                  {isSurveyComplete ? 'Save Survey ✨' : 'Answer All Questions'}
                </button>
              </div>
                </>
              )}
            </div>
            {/* Interests Section */}
            <div className="profile-section" style={{ marginBottom: '16px' }}>
              <button
                type="button"
                onClick={() => setExpandedSections({...expandedSections, interests: !expandedSections.interests})}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: 'none',
                  border: 'none',
                  padding: '0 0 12px 0',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'var(--text-primary)'
                }}
              >
                <span>🎯 Interests ({profileForm.interests.length} selected)</span>
                <span style={{ fontSize: '18px', transition: 'transform 0.2s', transform: expandedSections.interests ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                  ▼
                </span>
              </button>

              {expandedSections.interests && (
                <>
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                marginBottom: '10px'
              }}>
                <button
                  type="button"
                  onClick={() => { setShowInterestModal(true); setActiveInterestTab(null); setInterestSearch(''); }}
                  style={{
                    background: 'none', border: 'none', color: 'var(--primary)', fontSize: '13px',
                    cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600
                  }}
                >
                  Edit
                </button>
              </div>

              {/* Display selected interests grouped by category */}
              {profileForm.interests.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {Object.entries(interestsData).map(([category, interests]) => {
                    const selected = interests.filter(i => profileForm.interests.includes(i));
                    if (selected.length === 0) return null;
                    return (
                      <div key={category}>
                        <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--primary)', marginBottom: '5px' }}>
                          {category}
                          <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> ({selected.length})</span>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginLeft: '4px' }}>
                          {selected.map(interest => (
                            <span key={interest} style={{
                              padding: '3px 8px', borderRadius: '10px',
                              background: 'var(--surface-dim)', color: 'var(--primary)',
                              fontSize: '11px', fontWeight: 500
                            }}>
                              {interest}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '16px 0' }}>
                  <p style={{ color: 'var(--text-muted)', fontSize: '13px', marginBottom: '10px' }}>No interests set yet</p>
                  <button
                    type="button"
                    onClick={() => { setShowInterestModal(true); setActiveInterestTab(null); setInterestSearch(''); }}
                    style={{
                      padding: '10px 20px', background: 'var(--primary)', color: 'white', border: 'none',
                      borderRadius: '20px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit'
                    }}
                  >
                    + Choose Your Interests
                  </button>
                </div>
              )}
                </>
              )}
            </div>

            {/* ═══════ INTEREST EDITOR MODAL ═══════ */}
            {showInterestModal && (
              <div style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)',
                zIndex: 9999, display: 'flex', flexDirection: 'column', alignItems: 'center'
              }}>
                <div style={{
                  maxWidth: '480px', width: '100%', margin: '0 auto', display: 'flex',
                  flexDirection: 'column', height: '100%', background: 'var(--card-bg)'
                }}>
                  {/* Modal header */}
                  <div style={{
                    padding: '14px 16px', borderBottom: '1px solid var(--border)',
                    display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0
                  }}>
                    <span style={{ fontSize: '18px' }}>🎯</span>
                    <h2 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '16px', fontWeight: 700, flex: 1 }}>
                      Edit Interests
                    </h2>
                    <span style={{
                      background: 'var(--surface-dim)', color: 'var(--primary)',
                      padding: '3px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: 700
                    }}>
                      {profileForm.interests.length} selected
                    </span>
                    <button
                      type="button"
                      onClick={() => setShowInterestModal(false)}
                      style={{
                        background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer',
                        color: 'var(--text-muted)', padding: '4px 8px', lineHeight: 1
                      }}
                    >
                      ✕
                    </button>
                  </div>

                  {/* Search */}
                  <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="text"
                        value={interestSearch}
                        onChange={e => { setInterestSearch(e.target.value); if (e.target.value.length >= 2) setActiveInterestTab(null); }}
                        placeholder="🔍 Search 260+ interests..."
                        className="spark-input"
                        style={{
                          width: '100%', padding: '10px 36px 10px 14px',
                          border: '1px solid var(--border)', borderRadius: '12px',
                          fontSize: '13px', outline: 'none', boxSizing: 'border-box',
                          background: 'var(--surface)', color: 'var(--text-primary)'
                        }}
                      />
                      {interestSearch && (
                        <button
                          type="button"
                          onClick={() => setInterestSearch('')}
                          style={{
                            position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)',
                            background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '16px'
                          }}
                        >
                          ×
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Search results */}
                  {interestSearch.length >= 2 ? (
                    <div style={{ flex: 1, overflow: 'auto', padding: '12px 16px' }}>
                      {(() => {
                        const searchLower = interestSearch.toLowerCase().trim();
                        const results = [];
                        Object.entries(interestsData).forEach(([category, interests]) => {
                          interests.forEach(interest => {
                            if (interest.toLowerCase().includes(searchLower)) {
                              results.push({ category, interest });
                            }
                          });
                        });
                        if (results.length === 0) {
                          return (
                            <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>
                              <p style={{ fontSize: '13px' }}>No matching interests</p>
                            </div>
                          );
                        }
                        return (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                            {results.slice(0, 30).map(({ category, interest }) => {
                              const isSelected = profileForm.interests.includes(interest);
                              return (
                                <button key={interest} type="button" onClick={() => toggleInterest(interest)}
                                  style={{
                                    display: 'flex', alignItems: 'center', gap: '5px', padding: '8px 12px',
                                    borderRadius: '16px', border: `1.5px solid ${isSelected ? 'var(--primary)' : 'var(--border)'}`,
                                    cursor: 'pointer', fontSize: '12px', fontWeight: 600, fontFamily: 'inherit',
                                    background: isSelected ? 'var(--primary)' : 'var(--surface)',
                                    color: isSelected ? 'white' : 'var(--text-primary)', transition: 'all 0.15s'
                                  }}>
                                  {interest}
                                  {isSelected && ' ✓'}
                                </button>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </div>
                  ) : (
                    <>
                      {/* Group tabs — horizontal scrollable */}
                      <div style={{ borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
                        <div style={{
                          display: 'flex', overflow: 'auto', padding: '8px 12px', gap: '6px',
                          scrollbarWidth: 'none', msOverflowStyle: 'none'
                        }}>
                          <button
                            type="button"
                            onClick={() => setActiveInterestTab(null)}
                            style={{
                              padding: '6px 12px', borderRadius: '16px', border: 'none', cursor: 'pointer',
                              fontSize: '11px', fontWeight: 700, fontFamily: 'inherit', whiteSpace: 'nowrap', flexShrink: 0,
                              background: !activeInterestTab ? 'var(--surface-dim)' : 'var(--surface)',
                              color: !activeInterestTab ? 'var(--primary)' : 'var(--text-muted)', transition: 'all 0.15s'
                            }}
                          >
                            All Groups
                          </button>
                          {Object.entries(interestsData).map(([category, interests]) => {
                            const count = interests.filter(i => profileForm.interests.includes(i)).length;
                            const isActive = activeInterestTab === category;
                            const emoji = category.split(' ')[0];
                            const label = category.replace(/^[^\s]+\s/, '').split(' & ')[0].split(' ')[0];
                            return (
                              <button key={category} type="button" onClick={() => setActiveInterestTab(category)}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: '4px',
                                  padding: '6px 10px', borderRadius: '16px', border: 'none', cursor: 'pointer',
                                  fontSize: '11px', fontWeight: 600, fontFamily: 'inherit', whiteSpace: 'nowrap', flexShrink: 0,
                                  background: isActive ? 'var(--surface-dim)' : 'var(--surface)',
                                  color: isActive ? 'var(--primary)' : 'var(--text-muted)', transition: 'all 0.15s'
                                }}
                              >
                                {emoji} {label}
                                {count > 0 && (
                                  <span style={{
                                    background: 'var(--surface-dim)', color: 'var(--primary)',
                                    padding: '1px 5px', borderRadius: '8px', fontSize: '10px', fontWeight: 700
                                  }}>{count}</span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Content area */}
                      <div style={{ flex: 1, overflow: 'auto', padding: '12px 16px' }}>
                        {!activeInterestTab ? (
                          /* All Groups overview */
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {Object.entries(interestsData).map(([category, interests]) => {
                              const count = interests.filter(i => profileForm.interests.includes(i)).length;
                              const hasSelected = count > 0;
                              return (
                                <div key={category}
                                  style={{
                                    display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px',
                                    background: hasSelected ? 'var(--surface-dim)' : 'var(--surface)',
                                    borderRadius: '12px',
                                    border: `1px solid ${hasSelected ? 'var(--primary)' : 'var(--border)'}`,
                                    cursor: 'pointer', transition: 'all 0.15s'
                                  }}
                                  onClick={() => setActiveInterestTab(category)}
                                >
                                  <span style={{ fontSize: '20px', flexShrink: 0 }}>{category.split(' ')[0]}</span>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{
                                      color: hasSelected ? 'var(--primary)' : 'var(--text-primary)',
                                      fontSize: '13px', fontWeight: 600
                                    }}>
                                      {category.replace(/^[^\s]+\s/, '')}
                                    </div>
                                    <div style={{ color: 'var(--text-muted)', fontSize: '11px' }}>
                                      {interests.length} interests{count > 0 && <span style={{ color: 'var(--primary)' }}> · {count} picked</span>}
                                    </div>
                                  </div>
                                  <span style={{ color: 'var(--text-muted)', fontSize: '14px', flexShrink: 0 }}>›</span>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          /* Single group detail */
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                              <button type="button" onClick={() => setActiveInterestTab(null)}
                                style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '4px', fontSize: '16px' }}>
                                ←
                              </button>
                              <span style={{ fontSize: '18px' }}>{activeInterestTab.split(' ')[0]}</span>
                              <h3 style={{ margin: 0, color: 'var(--primary)', fontSize: '15px', fontWeight: 700, flex: 1 }}>
                                {activeInterestTab.replace(/^[^\s]+\s/, '')}
                              </h3>
                              {(() => {
                                const interests = interestsData[activeInterestTab] || [];
                                const allSelected = interests.every(i => profileForm.interests.includes(i));
                                return (
                                  <button type="button"
                                    onClick={() => {
                                      if (allSelected) {
                                        setProfileForm(prev => ({
                                          ...prev,
                                          interests: prev.interests.filter(i => !interests.includes(i))
                                        }));
                                      } else {
                                        setProfileForm(prev => ({
                                          ...prev,
                                          interests: [...new Set([...prev.interests, ...interests])]
                                        }));
                                      }
                                    }}
                                    style={{
                                      padding: '5px 10px', borderRadius: '10px', border: 'none', cursor: 'pointer',
                                      fontSize: '11px', fontWeight: 600, fontFamily: 'inherit',
                                      background: 'var(--surface-dim)', color: 'var(--primary)'
                                    }}
                                  >
                                    {allSelected ? 'Deselect All' : 'Select All'}
                                  </button>
                                );
                              })()}
                            </div>

                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                              {(interestsData[activeInterestTab] || []).map(interest => {
                                const isSelected = profileForm.interests.includes(interest);
                                return (
                                  <button key={interest} type="button" onClick={() => toggleInterest(interest)}
                                    style={{
                                      padding: '8px 14px', borderRadius: '16px',
                                      border: `1.5px solid ${isSelected ? 'var(--primary)' : 'var(--border)'}`,
                                      cursor: 'pointer', fontSize: '12px', fontWeight: 500, fontFamily: 'inherit',
                                      background: isSelected ? 'var(--primary)' : 'var(--surface)',
                                      color: isSelected ? 'white' : 'var(--text-primary)',
                                      transition: 'all 0.12s'
                                    }}>
                                    {interest}{isSelected ? ' ✓' : ''}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {/* Save footer */}
                  <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)', flexShrink: 0 }}>
                    <button type="button" onClick={() => setShowInterestModal(false)}
                      style={{
                        width: '100%', padding: '12px', background: 'var(--primary)', color: 'white',
                        border: 'none', borderRadius: '12px', fontSize: '14px', fontWeight: 600,
                        cursor: 'pointer', fontFamily: 'inherit'
                      }}>
                      Done — {profileForm.interests.length} interests selected
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Discovery Preferences Section */}
            <div className="profile-section">
              <button
                type="button"
                onClick={() => setExpandedSections({...expandedSections, discovery: !expandedSections.discovery})}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: 'none',
                  border: 'none',
                  padding: '0 0 12px 0',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'var(--text-primary)'
                }}
              >
                <span>🔍 Discovery Preferences</span>
                <span style={{ fontSize: '18px', transition: 'transform 0.2s', transform: expandedSections.discovery ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                  ▼
                </span>
              </button>

              {expandedSections.discovery && (
                <>
              {/* Age Range */}
              <div style={{ marginBottom: '18px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', marginBottom: '10px', color: 'var(--text-secondary)', fontWeight: 500 }}>
                  <Cake size={14} />
                  Age: <strong style={{ color: 'var(--text-primary)' }}>{profileForm.preferences.ageMin} - {profileForm.preferences.ageMax}</strong>
                </label>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <input
                    type="range"
                    min="18"
                    max="100"
                    value={profileForm.preferences.ageMin}
                    onChange={(e) => setProfileForm({
                      ...profileForm,
                      preferences: {...profileForm.preferences, ageMin: parseInt(e.target.value)}
                    })}
                    style={{ flex: 1 }}
                  />
                  <input
                    type="range"
                    min="18"
                    max="100"
                    value={profileForm.preferences.ageMax}
                    onChange={(e) => setProfileForm({
                      ...profileForm,
                      preferences: {...profileForm.preferences, ageMax: parseInt(e.target.value)}
                    })}
                    style={{ flex: 1 }}
                  />
                </div>
              </div>

              {/* Distance */}
              <div style={{ marginBottom: '18px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', marginBottom: '10px', color: 'var(--text-secondary)', fontWeight: 500 }}>
                  <MapPin size={14} />
                  Distance: <strong style={{ color: 'var(--text-primary)' }}>{profileForm.preferences.distance} km</strong>
                </label>
                <input
                  type="range"
                  min="5"
                  max="200"
                  step="5"
                  value={profileForm.preferences.distance}
                  onChange={(e) => setProfileForm({
                    ...profileForm,
                    preferences: {...profileForm.preferences, distance: parseInt(e.target.value)}
                  })}
                  style={{ width: '100%' }}
                />
              </div>

              {/* Gender Preference */}
              <div>
                <label style={{ display: 'block', fontSize: '12px', marginBottom: '10px', color: 'var(--text-secondary)', fontWeight: 500 }}>
                  Interested in
                </label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {['male', 'female', 'non-binary', 'other'].map((gender) => {
                    const isSelected = profileForm.preferences.interestedIn.includes(gender);
                    return (
                      <button
                        key={gender}
                        type="button"
                        onClick={() => toggleGenderPreference(gender)}
                        style={{
                          padding: '8px 18px',
                          background: isSelected ? 'var(--primary)' : 'var(--surface)',
                          color: isSelected ? 'white' : 'var(--text-secondary)',
                          border: `1.5px solid ${isSelected ? 'var(--primary)' : 'var(--border)'}`,
                          borderRadius: 'var(--radius-full)',
                          fontSize: '12px',
                          fontWeight: 600,
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        {gender.charAt(0).toUpperCase() + gender.slice(1)}
                      </button>
                    );
                  })}
                </div>
              </div>
                </>
              )}
            </div>

            {/* Save Button — sticky feel */}
            <div style={{ display: 'flex', gap: '10px', paddingTop: '8px', paddingBottom: '8px' }}>
              <button
                onClick={() => {
                  // Reset form to saved profile data when canceling
                  if (profile) {
                    setProfileForm({
                      bio: profile.bio || '',
                      interests: profile.interests || [],
                      occupation: profile.occupation || '',
                      education: profile.education || '',
                      height: profile.height?.toString() || '',
                      photos: profile.photos || [],
                      preferences: {
                        ageMin: profile.preferences?.minAge || 18,
                        ageMax: profile.preferences?.maxAge || 50,
                        distance: profile.preferences?.maxDistance || 50,
                        interestedIn: profile.preferences?.interestedIn || []
                      },
                      surveyResults: profile.surveyResults || surveyData,
                      personalityProfile: profile.personalityProfile || '',
                      mbtiType: profile.mbtiType || '',
                      kids: profile.kids || '',
                      religion: profile.religion || '',
                      pets: profile.pets || ''
                    });
                  }
                  setEditingProfile(false);
                  setCurrentView('discover');
                }}
                style={{
                  flex: 1,
                  padding: '14px',
                  background: 'var(--surface-alt)',
                  color: 'var(--text-secondary)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={onSaveProfile}
                disabled={loading}
                style={{
                  flex: 2,
                  padding: '14px',
                  background: loading ? 'var(--text-muted)' : 'var(--primary)',
                  color: 'white',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {loading ? 'Saving...' : 'Save Profile'}
              </button>
            </div>
          </div>
  );
}
